import { useCallback, useRef, useState } from 'react'
import { router } from '@inertiajs/react'
import type { DragEndEvent, DragStartEvent, UniqueIdentifier } from '@dnd-kit/core'
import {
  Kanban,
  KanbanBoard as KanbanBoardPrimitive,
  KanbanColumn,
  KanbanItem,
  KanbanOverlay,
} from '@/components/ui/kanban'
import type CrmFundraisingOpportunity from '@/types/serializers/CrmFundraisingOpportunity'
import type { OpportunityStatus } from '@/types'
import { KanbanCard } from './KanbanCard'
import { OPPORTUNITY_STATUS_LABELS } from '@/lib/fundraising-labels'
import { Badge } from '@/components/ui/badge'

const PIPELINE_COLUMNS: OpportunityStatus[] = [
  'prospection',
  'qualification',
  'proposal',
  'negotiation',
  'won',
  'lost',
  'closed',
]

const TERMINAL_STATUSES: OpportunityStatus[] = ['lost', 'closed']

const VALID_TRANSITIONS: Record<
  OpportunityStatus,
  { target: OpportunityStatus; event: string }[]
> = {
  prospection: [
    { target: 'qualification', event: 'qualify' },
    { target: 'lost', event: 'lose' },
  ],
  qualification: [
    { target: 'proposal', event: 'propose' },
    { target: 'lost', event: 'lose' },
  ],
  proposal: [
    { target: 'negotiation', event: 'negotiate' },
    { target: 'lost', event: 'lose' },
  ],
  negotiation: [
    { target: 'won', event: 'win' },
    { target: 'lost', event: 'lose' },
  ],
  won: [{ target: 'closed', event: 'close' }],
  lost: [],
  closed: [],
}

function findTransitionEvent(
  source: OpportunityStatus,
  target: OpportunityStatus
): string | null {
  const match = VALID_TRANSITIONS[source]?.find((t) => t.target === target)
  return match?.event ?? null
}

const COLUMN_HEADER_COLORS: Record<OpportunityStatus, string> = {
  prospection: 'bg-blue-500',
  qualification: 'bg-indigo-500',
  proposal: 'bg-violet-500',
  negotiation: 'bg-amber-500',
  won: 'bg-emerald-500',
  lost: 'bg-red-400',
  closed: 'bg-gray-400',
}

type ColumnMap = Record<string, CrmFundraisingOpportunity[]>

function groupByStatus(opportunities: CrmFundraisingOpportunity[]): ColumnMap {
  const groups: ColumnMap = {}
  for (const col of PIPELINE_COLUMNS) groups[col] = []
  for (const opp of opportunities) {
    const key = ((opp as Record<string, unknown>).status_key ?? opp.status) as string
    if (groups[key]) groups[key].push(opp)
  }
  return groups
}

function findColumn(columns: ColumnMap, id: UniqueIdentifier): string | null {
  for (const [col, items] of Object.entries(columns)) {
    if (items.some((o) => o.id === id)) return col
  }
  return null
}

interface KanbanBoardComponentProps {
  opportunities: CrmFundraisingOpportunity[]
  onCardClick?: (id: number) => void
}

export function KanbanBoardComponent({ opportunities, onCardClick }: KanbanBoardComponentProps) {
  const [columns, setColumns] = useState<ColumnMap>(() => groupByStatus(opportunities))
  const snapshotRef = useRef<ColumnMap | null>(null)
  const [activeOpp, setActiveOpp] = useState<CrmFundraisingOpportunity | null>(null)

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      snapshotRef.current = structuredClone(columns)
      const col = findColumn(columns, event.active.id)
      if (col) {
        const opp = columns[col].find((o) => o.id === event.active.id)
        setActiveOpp(opp ?? null)
      }
    },
    [columns]
  )

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveOpp(null)
      const snapshot = snapshotRef.current
      snapshotRef.current = null

      if (!snapshot || !event.over) return

      const sourceCol = findColumn(snapshot, event.active.id) as OpportunityStatus | null
      const destCol = findColumn(columns, event.active.id) as OpportunityStatus | null

      if (!sourceCol || !destCol || sourceCol === destCol) return

      const transitionEvent = findTransitionEvent(sourceCol, destCol)
      if (!transitionEvent) {
        setColumns(snapshot)
        return
      }

      router.post(
        `/fundraising/opportunities/${event.active.id}/transition`,
        { event: transitionEvent },
        {
          preserveScroll: true,
          onError: () => { if (snapshot) setColumns(snapshot) },
        }
      )
    },
    [columns]
  )

  return (
    <Kanban
      value={columns}
      onValueChange={(val) => setColumns(val as ColumnMap)}
      getItemValue={(item) => item.id}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <KanbanBoardPrimitive className="flex gap-3 pb-2">
        {PIPELINE_COLUMNS.map((status) => {
          const items = columns[status] ?? []
          const isTerminal = TERMINAL_STATUSES.includes(status)

          return (
            <KanbanColumn
              key={status}
              value={status}
              className={`flex-1 min-w-0 rounded-xl border-0 bg-muted/40 ${isTerminal ? 'opacity-50' : ''}`}
            >
              {/* Column header */}
              <div className="flex items-center justify-between px-2.5 py-2">
                <div className="flex items-center gap-2 min-w-0">
                  <div className={`h-2.5 w-2.5 rounded-full shrink-0 ${COLUMN_HEADER_COLORS[status]}`} />
                  <h3 className="text-xs font-semibold text-foreground/80 truncate">
                    {OPPORTUNITY_STATUS_LABELS[status] ?? status}
                  </h3>
                </div>
                <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4.5 shrink-0 font-medium">
                  {items.length}
                </Badge>
              </div>

              {/* Cards */}
              <div className="flex flex-col gap-2 px-1.5 pb-2 min-h-[120px]">
                {items.length === 0 ? (
                  <div className="flex items-center justify-center h-[100px] rounded-lg border border-dashed border-muted-foreground/20">
                    <p className="text-[11px] text-muted-foreground/50">
                      Aucune opportunité
                    </p>
                  </div>
                ) : (
                  items.map((opp) => (
                    <KanbanItem
                      key={opp.id}
                      value={opp.id}
                      // Whole-card drag activator. The 8px sensor
                      // activation distance in kanban.tsx separates
                      // click (open sheet) from drag (move column).
                      asHandle={!isTerminal}
                      disabled={isTerminal}
                      className="cursor-pointer"
                      onClick={() => onCardClick?.(opp.id)}
                    >
                      <KanbanCard opportunity={opp} />
                    </KanbanItem>
                  ))
                )}
              </div>
            </KanbanColumn>
          )
        })}
      </KanbanBoardPrimitive>

      <KanbanOverlay>
        {activeOpp && <KanbanCard opportunity={activeOpp} isDragging />}
      </KanbanOverlay>
    </Kanban>
  )
}
